import { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { opsApi } from '../../api/client';
import { PdfImportQueue } from './PdfImportQueue';
import { ExportNowPreflight, ProcessPendingPreflight, ProcessPendingResults } from './ProcessPendingPanel';
import { formatCurrency } from '../../lib/currency';
import { Play, Check, X, FileText, AlertTriangle, Eye, RefreshCw, ChevronDown, ChevronUp, Pencil, Save } from 'lucide-react';
// `useLocalState` is just an alias for `useState` used inside the
// nested `InvoiceDetail` component so its hooks stay visually distinct
// from the parent's. Both reference the same React export.
const useLocalState = useState;

const INV_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600', parsed: 'bg-blue-100 text-blue-700', needs_review: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-green-100 text-green-700', approved: 'bg-green-100 text-green-700', exported: 'bg-purple-100 text-purple-700',
  duplicate_blocked: 'bg-red-100 text-red-700', error: 'bg-red-100 text-red-700',
};
const EXP_STATUS_COLORS = { PENDING: 'bg-yellow-100 text-yellow-700', PROCESSING: 'bg-blue-100 text-blue-700', EXPORTED: 'bg-green-100 text-green-700', FAILED: 'bg-red-100 text-red-700' };

// An operator override alone says nothing about the Peppol delivery plan.
// Use only explicit no-Peppol fallback evidence on a successful export.
function isBookedWithoutPeppol(exp, deliveryStatus) {
  return exp?.status === 'EXPORTED' && (
    exp.overrideType === 'peppol_unavailable_no_peppol'
    || exp.peppolStatus === 'skipped_unavailable'
    || deliveryStatus?.status === 'skipped_unavailable'
  );
}

// Stable client-only ids for the editable invoice-line rows. Bound to
// the row's React identity so typing in one input never re-mounts a
// sibling row (which would steal focus). Stripped from the payload
// before submit; the backend never sees `_uid`.
let _lineUidCounter = 0;
const nextLineUid = () => `line-${++_lineUidCounter}`;

function StatCard({ label, value, color = 'text-gray-900' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );
}

export default function OpsAccounting() {
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [exports, setExports] = useState([]);
  const [readErrors, setReadErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [serverProcessing, setServerProcessing] = useState({ live: 0, stale: 0 });
  const [processPreview, setProcessPreview] = useState(null);
  const [exportPreview, setExportPreview] = useState(null);
  const [exportRunning, setExportRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [fallbackPreview, setFallbackPreview] = useState(null);
  const [fallbackRunning, setFallbackRunning] = useState(false);
  const fallbackPreviewSequence = useRef(0);
  const fallbackInFlight = useRef(false);
  // A lost response must never offer a second send from this page. Server claims
  // enforce the same one-shot rule across refreshes, tabs and other operators.
  const fallbackSubmitted = useRef(new Set());
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [view, setView] = useState('actionable'); // 'actionable' | 'exported' | 'external' | 'archived'
  const reloadSequence = useRef(0);

  const [yukiConfig, setYukiConfig] = useState(null);

  const reload = useCallback(async (detailId = null) => {
    const sequence = ++reloadSequence.current;
    setInvoices(null);
    setReadErrors({});
    if (detailId) setDetailData(null);
    const [dash, inv, exp, ycfg, detail, proc] = await Promise.allSettled([
      opsApi.accountingDashboard().then(r => r.data),
      opsApi.accountingInvoices({ view }).then(r => r.data),
      opsApi.accountingExports().then(r => r.data),
      opsApi.yukiConfig().then(r => r.data),
      detailId ? opsApi.accountingInvoiceDetail(detailId).then(r => r.data) : Promise.resolve(null),
      opsApi.yukiProcessingStatus().then(r => r.data),
    ]);
    if (sequence !== reloadSequence.current) return;
    const succeeded = result => result.status === 'fulfilled';
    setDashboard(succeeded(dash) ? dash.value : null);
    setInvoices(succeeded(inv) ? inv.value : null);
    setExports(succeeded(exp) ? exp.value : []);
    setYukiConfig(succeeded(ycfg) ? ycfg.value : null);
    // Read-only server-side processing status: live PROCESSING rows block a second Process click.
    setServerProcessing(succeeded(proc)
      ? { live: (proc.value.processingCount || 0) - (proc.value.staleCount || 0), stale: proc.value.staleCount || 0 }
      : { live: 0, stale: 0, unavailable: true });
    setReadErrors({
      dashboard: !succeeded(dash) && 'Overzicht kon niet worden geladen. Ververs opnieuw.',
      invoices: !succeeded(inv) && 'Factuurlijst kon niet worden geladen. Ververs opnieuw.',
      exports: !succeeded(exp) && 'Exporthistoriek kon niet worden geladen. Exportstatussen zijn niet beschikbaar. Ververs opnieuw.',
      config: !succeeded(ycfg) && 'Yuki-instellingen konden niet worden geladen. Ververs opnieuw.',
      detail: !succeeded(detail) && 'Factuurdetail kon niet worden geladen. Open het detail opnieuw.',
    });
    if (detailId) {
      const stillVisible = succeeded(inv) && inv.value.some(item => item._id === detailId);
      const refreshedDetail = succeeded(detail) ? detail.value : null;
      setDetailData(stillVisible ? refreshedDetail : null);
      setSelectedInvoice(stillVisible && refreshedDetail ? detailId : null);
    }
  }, [view]);

  useEffect(() => { reload().finally(() => setLoading(false)); }, [reload]);

  // While the server still shows live PROCESSING rows (e.g. after a lost response),
  // poll the read-only status so counters/results refresh by themselves. Never re-POSTs.
  useEffect(() => {
    if (!serverProcessing.live || processing) return undefined;
    const t = setTimeout(() => reload(selectedInvoice), 5000);
    return () => clearTimeout(t);
  }, [serverProcessing.live, processing, reload, selectedInvoice]);

  const processPending = async () => {
    if (processing || serverProcessing.live > 0) return;
    setProcessing(true); setActionResult(null); setBatchResult(null);
    try {
      const { data } = await opsApi.yukiProcessPending(processPreview?.previewFingerprint);
      if (data.ok === false) setActionResult({ ok: false, message: data.message || 'Verwerking geweigerd door de server' });
      else {
        const s = data.peppolSummary || {};
        setActionResult({ ok: !data.failed, message: `Batch klaar: ${data.processed} geboekt in Yuki (${s.peppolRequestedDeliveryUnconfirmed || 0} met Peppol-aanvraag, aflevering onbevestigd), ${data.failed} mislukt${data.skippedQuota ? `, ${data.skippedQuota} uitgesteld (Yuki-quota)` : ''}. Statussen hieronder zijn vers opgehaald.` });
        setBatchResult(data);
      }
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        setActionResult({ ok: false, unknown: true, message: `Uitkomst onbekend: geen (volledig) antwoord van de server${err.response ? ` (HTTP ${err.response.status})` : ''}. De batch kan nog lopen of al klaar zijn. De echte status wordt read-only opgehaald — er wordt NIET automatisch opnieuw verwerkt. Controleer de exportstatussen hieronder.` });
      } else if (err.response.status === 409 && ['preview_stale', 'preview_required'].includes(err.response.data?.detail?.code)) {
        setActionResult({ ok: false, message: `Niet verwerkt (geen enkele verzending): ${err.response.data.detail.message} Klik opnieuw op Process Pending voor een vers overzicht.` });
      } else {
        const det = err.response?.data?.detail;
        setActionResult({ ok: false, message: (det && typeof det === 'object' ? det.message || det.code : det) || 'Verwerking mislukt' });
      }
    } finally {
      setProcessPreview(null);
      await reload(selectedInvoice); // always show the real server state, whatever the response was
      setProcessing(false);
    }
  };

  const openProcessPreview = async () => {
    if (processing || serverProcessing.live > 0) return;
    setActionResult(null); setBatchResult(null);
    try {
      const { data } = await opsApi.yukiProcessPendingPreview();
      setProcessPreview(data);
    } catch (err) {
      setActionResult({ ok: false, message: 'Vooraf-overzicht kon niet worden geladen; niets verwerkt.' });
    }
  };

  const openDetail = async (id) => {
    if (selectedInvoice === id) { setSelectedInvoice(null); return; }
    try {
      const { data } = await opsApi.accountingInvoiceDetail(id);
      setDetailData(data);
      setSelectedInvoice(id);
    } catch (e) { setSelectedInvoice(null); }
  };

  const invoiceAction = async (id, action, exportId = null) => {
    setActionResult(null);
    try {
      if (action === 'approve') await opsApi.accountingInvoiceApprove(id);
      else if (action === 'confirm') await opsApi.accountingInvoiceConfirm(id);
      else if (action === 'block') await opsApi.accountingInvoiceBlock(id);
      else if (action === 'reopen') await opsApi.accountingInvoiceReopen(id);
      else if (action === 'export') {
        // Step 1 only: read-only plan for this one invoice; the POST happens in confirmExportNow.
        const { data } = await opsApi.accountingInvoiceExportNowPreview(id);
        setExportPreview({ ...data, invoiceId: id });
        return;
      }
      else if (action === 'archive') {
        await opsApi.accountingInvoiceArchive(id);
        setActionResult({ ok: true, message: 'Invoice archived' });
        setSelectedInvoice(null);
      }
      else if (action === 'restore') {
        await opsApi.accountingInvoiceRestore(id);
        setActionResult({ ok: true, message: 'Invoice restored' });
      }
      else if (action === 'reparse') {
        const { data } = await opsApi.accountingInvoiceReparse(id);
        setActionResult({ ok: true, message: `Reparsed: ${data.status} (${data.reviewReasons?.length || 0} issues)` });
      }
      else if (action === 'reparseAddressDryRun') {
        const { data } = await opsApi.accountingInvoiceReparseAddress(id, { apply: false });
        const changedCount = (data.changedFields || []).length;
        setActionResult({
          ok: true,
          message: changedCount
            ? `Adres-reparse (dry-run): ${changedCount} veld(en) zouden wijzigen — ${data.changedFields.join(', ')}.${data.alreadyExportedWarning ? ' ⚠ ' + data.alreadyExportedWarning : ''}`
            : `Adres-reparse (dry-run): geen wijzigingen.${data.alreadyExportedWarning ? ' ⚠ ' + data.alreadyExportedWarning : ''}`,
        });
      }
      else if (action === 'reparseAddressApply') {
        const { data } = await opsApi.accountingInvoiceReparseAddress(id, { apply: true });
        const changedCount = (data.changedFields || []).length;
        setActionResult({
          ok: true,
          message: data.noChanges
            ? 'Adres-reparse toegepast — geen wijzigingen (idempotent).'
            : `Adres-reparse toegepast — ${changedCount} veld(en) bijgewerkt: ${data.changedFields.join(', ')}.${data.alreadyExportedWarning ? ' ⚠ ' + data.alreadyExportedWarning : ''}`,
        });
      }
      else if (action === 'reparseCustomerNameDryRun') {
        const { data } = await opsApi.accountingInvoiceReparseCustomerName(id, { apply: false });
        const changedCount = (data.changedFields || []).length;
        setActionResult({
          ok: true,
          message: changedCount
            ? `Klantnaam-resync (dry-run): ${changedCount} veld(en) zouden wijzigen — ${data.changedFields.join(', ')}. Proposed: customerName='${data.proposed.customerName}', contactName='${data.proposed.contactName}'.${data.alreadyExportedWarning ? ' ⚠ ' + data.alreadyExportedWarning : ''}`
            : `Klantnaam-resync (dry-run): geen wijzigingen.${data.alreadyExportedWarning ? ' ⚠ ' + data.alreadyExportedWarning : ''}`,
        });
      }
      else if (action === 'reparseCustomerNameApply') {
        const { data } = await opsApi.accountingInvoiceReparseCustomerName(id, { apply: true });
        const changedCount = (data.changedFields || []).length;
        setActionResult({
          ok: true,
          message: data.noChanges
            ? 'Klantnaam-resync toegepast — geen wijzigingen (idempotent).'
            : `Klantnaam-resync toegepast — ${changedCount} veld(en) bijgewerkt: ${data.changedFields.join(', ')}.${data.alreadyExportedWarning ? ' ⚠ ' + data.alreadyExportedWarning : ''}`,
        });
      }
      else if (action === 'retry' && exportId) {
        const { data } = await opsApi.yukiRetry(exportId);
        setActionResult({ ok: data.success, message: data.message || (data.success ? 'Export retried successfully' : 'Retry failed') });
      }
      else if (action === 'operatorFallback' && exportId) {
        if (fallbackInFlight.current || fallbackSubmitted.current.has(exportId)) {
          setActionResult({ ok: false, message: 'Deze eenmalige actie is al gestart. Controleer de opgeslagen status; niet opnieuw verzenden.' });
          return;
        }
        const sequence = ++fallbackPreviewSequence.current;
        setFallbackPreview(null);
        const { data } = await opsApi.yukiOperatorFallbackPreview(exportId);
        if (sequence !== fallbackPreviewSequence.current) return;
        const validHash = value => typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
        if (data.eligible !== true || data.yukiEnabled !== true || data.invoiceId !== id
            || data.originalExportId !== exportId || !data.invoiceNumber
            || ![data.preStateHash, data.payloadSHA256, data.businessFieldsHash].every(validHash)
            || data.writesPerformed !== 0 || data.externalCallMade !== false) {
          setActionResult({ ok: false, message: `Exporteren zonder Peppol niet beschikbaar: ${data.blockedCode || 'voorcontrole niet geldig'}. Niets verzonden.` });
          return;
        }
        setFallbackPreview(data);
        return;
      }
      await reload();
      if (selectedInvoice === id) {
        // Refresh detail
        try {
          const { data } = await opsApi.accountingInvoiceDetail(id);
          setDetailData(data);
        } catch (e) { /* detail refresh is best-effort; state stays consistent via reload() above */ }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'object' ? detail?.message || detail?.code : detail;
      setActionResult({ ok: false, message: message || `Action failed: ${action}` });
    }
  };

  const confirmOperatorFallback = async (operatorConfirmedAbsent) => {
    const preview = fallbackPreview;
    if (operatorConfirmedAbsent !== true || !preview || fallbackInFlight.current
        || fallbackSubmitted.current.has(preview.originalExportId)) return;
    fallbackInFlight.current = true;
    fallbackSubmitted.current.add(preview.originalExportId);
    setFallbackRunning(true);
    setActionResult(null);
    try {
      const { data } = await opsApi.yukiRetryWithoutPeppol(preview.originalExportId, {
        confirm: true,
        operatorConfirmedAbsent: true,
        invoiceNumber: preview.invoiceNumber,
        confirmation: `EXPORT WITHOUT PEPPOL ${preview.invoiceNumber}`,
        preStateHash: preview.preStateHash,
      });
      setActionResult({
        ok: data.success === true,
        message: data.success === true
          ? `Factuur ${preview.invoiceNumber}: geboekt in Yuki — zonder Peppol. De oorspronkelijke Peppol-fout blijft in de historiek.`
          : `Factuur ${preview.invoiceNumber}: ${data.message || 'boeking niet bevestigd'}. Geen automatische tweede poging; controleer de opgeslagen status.`,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'object' ? detail?.message || detail?.code : detail;
      const unknown = !err.response || err.response.status >= 500;
      setActionResult({
        ok: false, unknown,
        message: unknown
          ? `Uitkomst van factuur ${preview.invoiceNumber} onbekend. Niet opnieuw verzenden; controleer Commerce en Yuki read-only.`
          : `Actie niet bevestigd: ${message || 'server heeft de actie geweigerd'}. Controleer de opgeslagen status; niet opnieuw verzenden.`,
      });
    } finally {
      setFallbackPreview(null);
      await reload(preview.invoiceId);
      fallbackInFlight.current = false;
      setFallbackRunning(false);
    }
  };

  const confirmExportNow = async () => {
    if (!exportPreview || exportRunning) return;
    const { invoiceId, exportFingerprint } = exportPreview;
    setExportRunning(true);
    setActionResult(null);
    try {
      const { data } = await opsApi.accountingInvoiceExportNow(invoiceId, exportFingerprint);
      const peppol = data?.peppolRequested ? ' — Peppol aangevraagd bij Yuki, aflevering onbevestigd (controleer in Yuki-UI)' : data?.success ? ' — alleen geboekt (Peppol niet gevraagd)' : '';
      setActionResult({ ok: data?.success !== false, message: `${data?.message || 'Export uitgevoerd'}${peppol}` });
      setExportPreview(null);
      await reload();
      if (selectedInvoice === invoiceId) {
        try { const d = await opsApi.accountingInvoiceDetail(invoiceId); setDetailData(d.data); } catch (e) { /* best-effort */ }
      }
    } catch (err) {
      const det = err.response?.data?.detail;
      if (err.response?.status === 409 && ['preview_stale', 'preview_required'].includes(det?.code)) {
        setActionResult({ ok: false, message: `Niet verzonden (geen enkele Yuki-aanroep): ${det.message} Klik opnieuw op Export Now voor een vers plan.` });
      } else {
        setActionResult({ ok: false, message: (det && typeof det === 'object' ? det.message || det.code : det) || 'Export Now mislukt' });
      }
      setExportPreview(null);
    } finally {
      setExportRunning(false);
    }
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin" /></div>;

  return (
    <div data-testid="ops-accounting" className="p-8">
      <div className="mb-6"><h1 className="text-2xl font-bold text-gray-900">Accounting Cockpit</h1>
        <p className="text-sm text-gray-500 mt-1">Bridge: PDF import → Sales Invoices → Yuki Export</p></div>

      <button data-testid="refresh-accounting" onClick={() => reload(selectedInvoice)}
        className="mb-4 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Verversen
      </button>

      {Object.entries(readErrors).filter(([, message]) => message).map(([section, message]) => (
        <div key={section} role="alert" data-testid={`accounting-read-error-${section}`}
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {message}
        </div>
      ))}

      {/* Dashboard KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <StatCard label="Total Invoices" value={dashboard.totalInvoices} />
          <StatCard label="Pending Review" value={dashboard.pendingReview} color={dashboard.pendingReview > 0 ? 'text-amber-600' : 'text-gray-900'} />
          <StatCard label="Pending Export" value={dashboard.pendingExport} color={dashboard.pendingExport > 0 ? 'text-yellow-600' : 'text-gray-900'} />
          <StatCard label="Failed Export" value={dashboard.failedExport} color={dashboard.failedExport > 0 ? 'text-red-600' : 'text-gray-900'} />
          <StatCard label="Exported Today" value={dashboard.exportedToday} color="text-green-600" />
          <StatCard label="Duplicates" value={dashboard.duplicatesFlagged} color={dashboard.duplicatesFlagged > 0 ? 'text-red-600' : 'text-gray-900'} />
        </div>
      )}

      {/* Yuki connector status (read-only — manage in System) */}
      {(dashboard?.yuki || yukiConfig) && (() => {
        const y = dashboard?.yuki || {};
        const yc = yukiConfig || {};
        const enabled = y.enabled || yc.yukiEnabled === 'true';
        const configured = y.configured || yc.configComplete;
        const connectorOk = y.connectorHealthy === true;
        return (
          <div className={`bg-white rounded-xl border p-4 mb-6 flex flex-wrap items-center gap-4 text-sm ${!enabled ? 'border-amber-200 bg-amber-50/50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${enabled && connectorOk ? 'bg-green-500' : enabled && configured ? 'bg-amber-500' : 'bg-gray-300'}`} />
              <span className="font-semibold text-gray-900">Yuki</span>
              {enabled && connectorOk && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">Authenticated</span>}
              {enabled && !connectorOk && configured && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">Config ready</span>}
              {!enabled && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-semibold">Not enabled</span>}
            </div>
            {y.failedCount > 0 && <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{y.failedCount} failed export(s)</span>}
            <a href="/ops/system" className="ml-auto text-xs text-blue-600 font-semibold hover:underline">Manage in System</a>
          </div>
        );
      })()}

      {/* Yuki daily-quota latch — blocks bulk retry while active */}
      {dashboard?.yuki?.quotaBlocked && (() => {
        const until = dashboard.yuki.quotaBlockedUntil;
        let untilLabel = '—';
        try {
          const d = until ? new Date(until) : null;
          if (d && !Number.isNaN(d.valueOf())) {
            untilLabel = d.toLocaleString('nl-BE', {
              timeZone: 'Europe/Brussels',
              hour: '2-digit', minute: '2-digit',
              day: '2-digit', month: '2-digit',
            });
          }
        } catch (_) { untilLabel = until || '—'; }
        return (
          <div
            data-testid="yuki-quota-banner"
            className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="space-y-1">
              <p className="font-semibold">
                Yuki daily limit reached. Do not retry today unless confirmed.
              </p>
              <p data-testid="yuki-quota-banner-unblock" className="text-xs">
                Auto-unblock at <span className="font-mono">{untilLabel}</span>{' '}
                (Europe/Brussels).
              </p>
              {dashboard.yuki.quotaLastError && (
                <p data-testid="yuki-quota-banner-error" className="text-xs text-red-700">
                  Last error: {dashboard.yuki.quotaLastError}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      <PdfImportQueue onImported={reload} onOpenInvoice={openDetail} />

      {actionResult && (
        <div role="status" data-testid="accounting-action-result"
          className={`mb-6 flex items-start gap-2 text-sm px-4 py-3 rounded-lg border ${actionResult.unknown ? 'bg-amber-50 border-amber-200 text-amber-900' : actionResult.ok !== false ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {actionResult.unknown ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : actionResult.ok !== false ? <Check className="w-4 h-4 mt-0.5 shrink-0" /> : <X className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{actionResult.message}</span>
        </div>
      )}

      {fallbackPreview && (
        <OperatorFallbackConfirmation preview={fallbackPreview} running={fallbackRunning}
          onConfirm={confirmOperatorFallback} onCancel={() => { fallbackPreviewSequence.current += 1; setFallbackPreview(null); }} />
      )}
      {exportPreview && (
        <ExportNowPreflight preview={exportPreview} running={exportRunning} onConfirm={confirmExportNow} onCancel={() => setExportPreview(null)} />
      )}
      {processPreview && (
        <ProcessPendingPreflight preview={processPreview} running={processing} onConfirm={processPending} onCancel={() => setProcessPreview(null)} />
      )}
      {batchResult && <ProcessPendingResults result={batchResult} onClose={() => setBatchResult(null)} />}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={openProcessPreview} data-testid="process-pending-btn"
          disabled={processing || !!processPreview || serverProcessing.live > 0 || !dashboard?.pendingExport || !dashboard?.yuki?.enabled || dashboard?.yuki?.quotaBlocked}
          title={dashboard?.yuki?.quotaBlocked ? 'Yuki daily limit reached — try again after midnight (Brussels)' : (!dashboard?.yuki?.enabled ? 'Yuki not enabled' : serverProcessing.live > 0 ? 'De server verwerkt nog exports; status wordt automatisch ververst' : '')}
          className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
          {processing || serverProcessing.live > 0 ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {processing ? 'Batch loopt — wacht op serverantwoord…' : serverProcessing.live > 0 ? `Server verwerkt nog ${serverProcessing.live} export(s)…` : `Process ${dashboard?.pendingExport || 0} Pending`}
        </button>
        {serverProcessing.live > 0 && (
          <span data-testid="server-processing-note" className="text-xs text-sky-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {serverProcessing.live} export(s) staan op PROCESSING bij de server; status wordt elke 5 s read-only ververst. Niet opnieuw starten.
          </span>
        )}
        {serverProcessing.stale > 0 && (
          <span data-testid="stale-processing-note" className="text-xs text-amber-700 flex items-center gap-1" title="PROCESSING ouder dan 15 min — geen automatische vrijgave; bestaande stale-lock-procedure.">
            <AlertTriangle className="w-3 h-3" /> {serverProcessing.stale} vastgelopen PROCESSING (stale) — nazicht nodig
          </span>
        )}
        {dashboard?.failedExport > 0 && (
          <button data-testid="retry-failed-btn" onClick={async () => {
            setRetrying(true); setActionResult(null);
            try {
              const { data } = await opsApi.yukiRetryAllFailed();
              if (data.ok === false && data.skippedQuota) {
                setActionResult({ ok: false, message: data.message || 'Yuki daily quota active — retries blocked' });
              } else {
                setActionResult({ ok: data.succeeded > 0, message: `Retried ${data.processed}: ${data.succeeded} succeeded, ${data.failed} failed${data.skippedQuota ? `, ${data.skippedQuota} deferred (quota)` : ''}` });
              }
              await reload();
            } catch (e) {
              setActionResult({ ok: false, message: e.response?.data?.detail || 'Retry failed' });
            } finally { setRetrying(false); }
          }} disabled={retrying || !dashboard?.yuki?.enabled || dashboard?.yuki?.quotaBlocked}
            title={dashboard?.yuki?.quotaBlocked ? 'Yuki daily limit reached — retries blocked until next midnight (Brussels)' : ''}
            className="px-4 py-2.5 border border-amber-300 bg-amber-50 rounded-lg text-sm font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50 flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} /> {retrying ? 'Retrying...' : `Retry ${dashboard?.failedExport} Failed`}
          </button>
        )}
        {dashboard?.failedExportBlocked > 0 && (
          <span data-testid="failed-blocked-note" className="text-xs text-gray-500 flex items-center gap-1" title="Failed exports linked to blocked or archived invoices are excluded from bulk retry.">
            <X className="w-3 h-3" /> {dashboard.failedExportBlocked} blocked
          </span>
        )}
        {!dashboard?.yuki?.enabled && (dashboard?.pendingExport > 0 || dashboard?.failedExport > 0) && (
          <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Yuki not enabled</span>
        )}
      </div>


      {/* Invoices list + detail */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {view === 'external' ? 'Extern verwerkt in Yuki' : view === 'archived' ? 'Archived Invoices' : view === 'exported' ? 'Exported Invoices' : 'Sales Invoices'}
          </h2>
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs" data-testid="invoice-view-filter" role="tablist">
            {[
              { key: 'actionable', label: 'Actionable', testid: 'view-actionable' },
              { key: 'external', label: `Extern verwerkt in Yuki (${dashboard?.externallyProcessedCount ?? '—'})`, testid: 'view-external' },
              { key: 'exported',   label: `Exported${dashboard?.exportedCount ? ` (${dashboard.exportedCount})` : ''}`, testid: 'view-exported' },
              { key: 'archived',   label: `Archived${dashboard?.archivedCount ? ` (${dashboard.archivedCount})` : ''}`, testid: 'view-archived' },
            ].map(opt => (
              <button
                key={opt.key}
                role="tab"
                aria-selected={view === opt.key}
                data-testid={opt.testid}
                onClick={() => { setSelectedInvoice(null); setView(opt.key); }}
                className={`px-3 py-1.5 font-semibold transition ${
                  view === opt.key
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400 uppercase"><tr>
              <th className="px-4 py-2 text-left">Number</th><th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Customer</th><th className="px-4 py-2 text-left">Source</th>
              <th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2 text-center">Status</th>
              <th className="px-4 py-2 text-center">Export</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {(invoices || []).map(inv => {
                const expEntry = exports.find(e => e.salesInvoiceId === inv._id);
                const externallyProcessed = inv.externalResolution?.status === 'externally_processed';
                const isBlocked = inv.status === 'duplicate_blocked';
                const expBlocked = expEntry && expEntry.status === 'FAILED' && (isBlocked || inv.archived);
                const isExpanded = selectedInvoice === inv._id;
                return (
                  <Fragment key={inv._id}>
                    <tr data-testid={`invoice-row-${inv.invoiceNumber || inv._id}`}
                        className={`hover:bg-gray-50/50 cursor-pointer ${inv.archived ? 'opacity-60' : ''}`}
                        onClick={() => openDetail(inv._id)}>
                      <td className="px-4 py-2.5 font-mono font-semibold text-gray-900">
                        {inv.invoiceNumber || '—'}
                        {inv.archived && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 uppercase">Archived</span>}
                        {isBlocked && !inv.archived && <span data-testid="blocked-badge" className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 uppercase">Blocked</span>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{inv.invoiceDate || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600">{inv.customerName || '—'}</td>
                      <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{inv.sourceType}</span></td>
                      <td className="px-4 py-2.5 text-right font-semibold">{formatCurrency(inv.totalInclVat)}</td>
                      <td className="px-4 py-2.5 text-center"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${externallyProcessed ? 'bg-teal-100 text-teal-800' : INV_STATUS_COLORS[inv.status] || 'bg-gray-100'}`}>{externallyProcessed ? 'Extern verwerkt in Yuki' : inv.status}</span></td>
                      <td className="px-4 py-2.5 text-center">
                        {readErrors.exports
                          ? <span className="text-red-700">Niet beschikbaar</span>
                          : expEntry
                          ? <span className={`text-xs font-semibold px-2 py-0.5 rounded ${EXP_STATUS_COLORS[expEntry.status] || 'bg-gray-100'}`}>
                              {externallyProcessed ? `Commerce: ${expEntry.status} (historisch)` : isBookedWithoutPeppol(expEntry) ? 'Geboekt in Yuki — zonder Peppol' : expEntry.status}{expBlocked ? ' · RETRY BLOCKED' : ''}
                            </span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">{isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}</td>
                    </tr>
                    {isExpanded && detailData && detailData._id === inv._id && (
                      <tr key={`${inv._id}-detail`} data-testid={`invoice-detail-row-${inv.invoiceNumber || inv._id}`}>
                        <td colSpan={8} className="bg-gray-50 border-t border-gray-200 p-4">
                          <InvoiceDetail inv={detailData}
                            onAction={(action, exportId) => invoiceAction(selectedInvoice, action, exportId)}
                            exportHistory={detailData.exportHistory || exports.filter(item => item.salesInvoiceId === inv._id)}
                            onSave={async (data) => {
                              setActionResult(null);
                              try {
                                const { data: result } = await opsApi.accountingInvoiceEdit(selectedInvoice, data);
                                setActionResult({ ok: true, message: `Saved: ${result.status} (${result.reviewReasons?.length || 0} issues)` });
                                await reload();
                                try { const { data: d } = await opsApi.accountingInvoiceDetail(selectedInvoice); setDetailData(d); } catch (e) { /* detail refresh is best-effort */ }
                                return true;
                              } catch (e) {
                                const det = e.response?.data?.detail;
                                const msg = (det && typeof det === 'object') ? (det.message || JSON.stringify(det)) : (det || 'Save failed');
                                setActionResult({ ok: false, message: msg });
                                return false; // keep the editor open — nothing was saved
                              }
                            }} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {invoices === null && !readErrors.invoices && <p className="text-center py-8 text-gray-400 text-sm">Factuurlijst laden...</p>}
        {invoices?.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">No invoices in this view</p>}
      </div>
    </div>
  );
}

// InvoiceDetail sub-component with edit mode
const IC = 'w-full px-2 py-1 text-xs rounded border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400';

export function InvoiceDetail({ inv, onAction, onSave, exportHistory = [] }) {
  const external = inv.externalResolution?.status === 'externally_processed' ? inv.externalResolution : null;
  const billing = typeof inv.billingAddress === 'object' ? inv.billingAddress : {};
  const shipping = typeof inv.shippingAddress === 'object' && inv.shippingAddress ? inv.shippingAddress : {};
  const seller = inv.seller || {};
  const [editing, setEditing] = useLocalState(false);
  const [form, setForm] = useLocalState(null);
  const [saving, setSaving] = useLocalState(false);
  const canEdit = !external && ['needs_review', 'parsed', 'export_failed'].includes(inv.status);

  const startEdit = () => {
    setForm({
      invoiceNumber: inv.invoiceNumber || '',
      customerName: inv.customerName || '',
      contactName: inv.contactName || '',
      orderNumber: inv.orderNumber || '',
      invoiceDate: inv.invoiceDate || '', customerVatNumber: inv.customerVatNumber || '',
      customerVatStatus: inv.customerVatStatus || '', customerId: inv.customerId || '',
      subtotalExVat: inv.subtotalExVat ?? inv.subtotalExclVat ?? 0, vatTotal: inv.vatTotal ?? 0, totalInclVat: inv.totalInclVat ?? 0,
      billingAddress: { street: billing.street || '', postalCode: billing.postalCode || '', city: billing.city || '', country: billing.country || '' },
      shippingAddress: {
        name: shipping.name || '',
        street: shipping.street || '',
        postalCode: shipping.postalCode || '',
        city: shipping.city || '',
        country: shipping.country || '',
      },
      shippingInstructions: inv.shippingInstructions || '',
      seller: { legalName: seller.legalName || '', vatNumber: seller.vatNumber || '', email: seller.email || '', iban: seller.iban || '', bic: seller.bic || '' },
      lines: (inv.lines || []).map(l => ({
        // Preserve ALL original line metadata (nullish merge — nothing is
        // dropped), keep the authoritative imported line total verbatim.
        ...l,
        _uid: nextLineUid(),
        _amountSource: 'imported',
        description: l.description ?? l.productName ?? '',
        sku: l.sku ?? '',
        quantity: l.quantity ?? 1,
        unitPriceExVat: l.unitPriceExVat ?? l.unitPrice ?? 0,
        // ?? — a legitimate 0% VAT rate must survive editing (|| turned 0 into 6).
        vatRate: l.vatRate ?? 6,
        // Read the SAME alias priority as the backend validator so the
        // operator edits the amount that actually counts.
        lineTotalExVat: l.lineTotalExclVat ?? l.lineTotalExVat ?? l.lineAmount ?? 0,
      })),
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    // Strip client-only fields (`_uid`, `_amountSource`) before sending.
    // eslint-disable-next-line no-unused-vars
    const cleanLines = (form.lines || []).map(({ _uid, _amountSource, ...rest }) => rest);
    // Nullish-merge nested subdocuments over the pristine invoice so
    // untouched nested metadata (extra address/seller keys) is preserved.
    const origBilling = typeof inv.billingAddress === 'object' && inv.billingAddress ? inv.billingAddress : {};
    const origShipping = typeof inv.shippingAddress === 'object' && inv.shippingAddress ? inv.shippingAddress : {};
    const ok = await onSave({
      ...form,
      lines: cleanLines,
      billingAddress: { ...origBilling, ...form.billingAddress },
      shippingAddress: { ...origShipping, ...form.shippingAddress },
      seller: { ...(inv.seller || {}), ...form.seller },
    });
    setSaving(false);
    // A failed save must NOT close edit mode or pretend success.
    if (ok !== false) setEditing(false);
  };

  // Cent-safe qty×unit (unit converted to integer cents first) so new
  // rows never carry float noise; NOT applied to untouched imported rows.
  const centSafeMul = (qty, unit) => {
    const q = Number(qty); const u = Number(unit);
    if (!Number.isFinite(q) || !Number.isFinite(u)) return 0;
    return Math.round(q * Math.round(u * 100)) / 100;
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { _uid: nextLineUid(), _amountSource: 'auto', description: '', sku: '', quantity: 1, unitPriceExVat: 0, vatRate: 6, lineTotalExVat: 0 }] }));
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, key, val) => setForm(f => ({
    ...f,
    lines: f.lines.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, [key]: val };
      if (key === 'lineTotalExVat') {
        // Explicit operator override of the exact net amount.
        next._amountSource = 'manual';
      } else if ((key === 'quantity' || key === 'unitPriceExVat') && l._amountSource !== 'manual') {
        // Qty/unit edits recompute the amount (no stale totals) unless
        // the operator explicitly overrode it.
        next.lineTotalExVat = centSafeMul(next.quantity, next.unitPriceExVat);
        next._amountSource = 'auto';
      }
      return next;
    }),
  }));

  if (editing && form && !external) return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-gray-500">Invoice Number</label><input data-testid="edit-invoice-number-input" className={IC} value={form.invoiceNumber} onChange={e => setForm(f => ({...f, invoiceNumber: e.target.value}))} /></div>
        <div><label className="text-xs text-gray-500">Customer Name</label><input className={IC} value={form.customerName} onChange={e => setForm(f => ({...f, customerName: e.target.value}))} /></div>
        <div><label className="text-xs text-gray-500">Contact Name (B2B)</label><input data-testid="edit-contact-name-input" className={IC} value={form.contactName} onChange={e => setForm(f => ({...f, contactName: e.target.value}))} /></div>
        <div><label className="text-xs text-gray-500">Order Number</label><input data-testid="edit-order-number-input" className={IC} value={form.orderNumber} onChange={e => setForm(f => ({...f, orderNumber: e.target.value}))} /></div>
        <div>
          <label className="text-xs text-gray-500">Buyer VAT</label>
          <input
            data-testid="edit-buyer-vat-input"
            className={IC}
            value={form.customerVatNumber}
            placeholder="BE0123456789"
            onChange={e => setForm(f => ({...f, customerVatNumber: e.target.value}))}
          />
          <p className="text-[10px] text-gray-400 mt-0.5">
            Trailing labels (e.g. "Methode Bancontact") are stripped on save.
          </p>
        </div>
        <div><label className="text-xs text-gray-500">Billing Street</label><input className={IC} value={form.billingAddress.street} onChange={e => setForm(f => ({...f, billingAddress: {...f.billingAddress, street: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">Postal Code</label><input className={IC} value={form.billingAddress.postalCode} onChange={e => setForm(f => ({...f, billingAddress: {...f.billingAddress, postalCode: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">City</label><input className={IC} value={form.billingAddress.city} onChange={e => setForm(f => ({...f, billingAddress: {...f.billingAddress, city: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">Country</label><input className={IC} value={form.billingAddress.country} onChange={e => setForm(f => ({...f, billingAddress: {...f.billingAddress, country: e.target.value}}))} /></div>
        <div className="sm:col-span-2 pt-2 mt-2 border-t border-gray-100"><p className="text-xs text-gray-500 uppercase font-semibold">Verzendadres</p></div>
        <div><label className="text-xs text-gray-500">Shipping Recipient / Company</label><input data-testid="edit-shipping-name-input" className={IC} value={form.shippingAddress?.name || ''} onChange={e => setForm(f => ({...f, shippingAddress: {...(f.shippingAddress||{}), name: e.target.value}}))} placeholder="Deve Pack NV" /></div>
        <div><label className="text-xs text-gray-500">Shipping Street</label><input data-testid="edit-shipping-street-input" className={IC} value={form.shippingAddress?.street || ''} onChange={e => setForm(f => ({...f, shippingAddress: {...(f.shippingAddress||{}), street: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">Shipping Postal Code</label><input data-testid="edit-shipping-postal-input" className={IC} value={form.shippingAddress?.postalCode || ''} onChange={e => setForm(f => ({...f, shippingAddress: {...(f.shippingAddress||{}), postalCode: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">Shipping City</label><input data-testid="edit-shipping-city-input" className={IC} value={form.shippingAddress?.city || ''} onChange={e => setForm(f => ({...f, shippingAddress: {...(f.shippingAddress||{}), city: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">Shipping Country</label><input data-testid="edit-shipping-country-input" className={IC} value={form.shippingAddress?.country || ''} onChange={e => setForm(f => ({...f, shippingAddress: {...(f.shippingAddress||{}), country: e.target.value}}))} /></div>
        <div className="sm:col-span-2"><label className="text-xs text-gray-500">Shipping Instructions</label><input data-testid="edit-shipping-instructions-input" className={IC} value={form.shippingInstructions || ''} onChange={e => setForm(f => ({...f, shippingInstructions: e.target.value}))} /></div>
        <div className="sm:col-span-2 pt-2 mt-2 border-t border-gray-100"><p className="text-xs text-gray-500 uppercase font-semibold">Seller</p></div>
        <div><label className="text-xs text-gray-500">Seller Legal Name</label><input className={IC} value={form.seller.legalName} onChange={e => setForm(f => ({...f, seller: {...f.seller, legalName: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">Seller VAT</label><input className={IC} value={form.seller.vatNumber} onChange={e => setForm(f => ({...f, seller: {...f.seller, vatNumber: e.target.value}}))} /></div>
        <div><label className="text-xs text-gray-500">Subtotal excl. VAT</label><input type="number" step="0.01" className={IC} value={form.subtotalExVat} onChange={e => setForm(f => ({...f, subtotalExVat: parseFloat(e.target.value)||0}))} /></div>
        <div><label className="text-xs text-gray-500">VAT Total</label><input type="number" step="0.01" className={IC} value={form.vatTotal} onChange={e => setForm(f => ({...f, vatTotal: parseFloat(e.target.value)||0}))} /></div>
        <div><label className="text-xs text-gray-500">Total incl. VAT</label><input type="number" step="0.01" className={IC} value={form.totalInclVat} onChange={e => setForm(f => ({...f, totalInclVat: parseFloat(e.target.value)||0}))} /></div>
      </div>
      {/* Lines editor */}
      <div>
        <div className="flex items-center justify-between mb-2"><p className="text-xs text-gray-500 uppercase font-semibold">Lines</p>
          <button onClick={addLine} className="text-xs text-blue-600 font-semibold hover:underline">+ Add line</button></div>
        {form.lines.map((line, i) => (
          <div key={line._uid} className="grid grid-cols-7 gap-1 mb-1">
            <input className={IC + ' col-span-2'} placeholder="Description" value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} />
            <input className={IC} placeholder="SKU" value={line.sku} onChange={e => updateLine(i, 'sku', e.target.value)} />
            <input type="number" className={IC} placeholder="Qty" data-testid={`edit-line-qty-input-${i}`} value={line.quantity} onChange={e => updateLine(i, 'quantity', parseInt(e.target.value)||0)} />
            <input type="number" step="0.01" className={IC} placeholder="Unit price" data-testid={`edit-line-unit-input-${i}`} value={line.unitPriceExVat} onChange={e => updateLine(i, 'unitPriceExVat', parseFloat(e.target.value)||0)} />
            <div>
              <input type="number" step="0.01" className={IC} placeholder="Line total ex VAT" data-testid={`edit-line-amount-input-${i}`} title="Exact net line amount — editing this overrides qty×unit" value={line.lineTotalExVat} onChange={e => updateLine(i, 'lineTotalExVat', parseFloat(e.target.value)||0)} />
              <span data-testid={`edit-line-amount-source-${i}`} className={'block text-[9px] leading-tight ' + (line._amountSource === 'manual' ? 'text-amber-600 font-semibold' : line._amountSource === 'auto' ? 'text-blue-500' : 'text-gray-400')}>
                {line._amountSource === 'manual' ? 'manual' : line._amountSource === 'auto' ? 'auto (qty×unit)' : 'imported (exact)'}
              </span>
            </div>
            <div className="flex gap-1"><input type="number" className={IC + ' flex-1'} placeholder="VAT%" data-testid={`edit-line-vat-input-${i}`} value={line.vatRate} onChange={e => updateLine(i, 'vatRate', parseInt(e.target.value)||0)} />
              <button onClick={() => removeLine(i)} className="text-red-400 hover:text-red-600 text-xs px-1">x</button></div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"><Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save & Validate'}</button>
        <button onClick={() => setEditing(false)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );

  // Read-only view (existing)
  const shippingHasAny = Boolean(
    shipping.street || shipping.postalCode || shipping.city || shipping.name || shipping.country,
  );
  const shippingInstructions = (inv.shippingInstructions || '').trim();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="invoice-billing-card">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Factuuradres</p>
          <div className="text-sm space-y-1">
            <p className="font-semibold" data-testid="buyer-customer-name">{inv.customerName || '—'}</p>
            {inv.contactName && <p className="text-gray-700" data-testid="buyer-contact-name">{inv.contactName}</p>}
            {billing.street && <p className="text-gray-600" data-testid="billing-street">{billing.street}</p>}
            {billing.postalCode && <p className="text-gray-600" data-testid="billing-postal-city">{billing.postalCode} {billing.city}</p>}
            {billing.country && <p className="text-gray-600" data-testid="billing-country">{billing.country}</p>}
            {inv.customerVatNumber && <p className="text-gray-500">VAT: {inv.customerVatNumber}</p>}
            {inv.customerVatStatus && <p className="text-gray-500">Status: {inv.customerVatStatus}</p>}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="invoice-shipping-card">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Verzendadres</p>
          {shippingHasAny ? (
            <div className="text-sm space-y-1">
              {shipping.name && <p className="font-semibold" data-testid="shipping-name">{shipping.name}</p>}
              {shipping.contactName && <p className="text-gray-700">{shipping.contactName}</p>}
              {shipping.street && <p className="text-gray-600" data-testid="shipping-street">{shipping.street}</p>}
              {shipping.postalCode && <p className="text-gray-600" data-testid="shipping-postal-city">{shipping.postalCode} {shipping.city}</p>}
              {shipping.country && <p className="text-gray-600" data-testid="shipping-country">{shipping.country}</p>}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic" data-testid="shipping-empty">Geen apart verzendadres in deze factuur.</p>
          )}
          {shippingInstructions && (
            <div className="mt-3 pt-2 border-t border-gray-100" data-testid="shipping-instructions-wrap">
              <p className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Leveringsinstructies</p>
              <p className="text-sm text-gray-700" data-testid="shipping-instructions">{shippingInstructions}</p>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Order & Payment</p>
          <div className="text-sm space-y-1">
            <p>Invoice: <span className="font-mono font-semibold">{inv.invoiceNumber || '—'}</span></p>
            <p>Order: <span className="font-mono">{inv.orderNumber || '—'}</span></p>
            <p>Date: {inv.invoiceDate || '—'}</p>
            {inv.paymentStatus && <p>Payment: <span className="font-semibold">{inv.paymentStatus}</span></p>}
            {inv.paymentMethod && <p>Method: {inv.paymentMethod}</p>}
          </div>
        </div>
      </div>
      {seller.legalName && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Seller</p>
          <div className="text-sm space-y-1">
            <p className="font-semibold">{seller.legalName}</p>
            {seller.street && <p className="text-gray-600">{seller.street}</p>}
            {(seller.postalCode || seller.city) && <p className="text-gray-600">{[seller.postalCode, seller.city].filter(Boolean).join(' ')}</p>}
            {seller.country && <p className="text-gray-600">{seller.country}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 pt-1 text-gray-500">
              <p>VAT: {seller.vatNumber || '—'}</p>
              {seller.email && <p>{seller.email}</p>}
              {seller.iban && <p>IBAN: {seller.iban}</p>}
              {seller.bic && <p>BIC: {seller.bic}</p>}
            </div>
          </div>
        </div>
      )}
      {inv.lines?.length > 0 && (
        <div><p className="text-xs text-gray-500 uppercase font-semibold mb-2">Lines</p>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-xs"><thead className="bg-gray-50 text-gray-500 uppercase"><tr><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-left">SKU</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Unit</th><th className="px-3 py-2 text-right">VAT%</th><th className="px-3 py-2 text-right">Total</th></tr></thead>
              <tbody className="divide-y divide-gray-50">{inv.lines.map((line, i) => (
                <tr key={`${line.description}-${i}`}><td className="px-3 py-2">{line.description || line.productName}</td><td className="px-3 py-2 font-mono text-gray-500">{line.sku || '—'}</td><td className="px-3 py-2 text-right">{line.quantity}</td><td className="px-3 py-2 text-right">{formatCurrency(line.unitPriceExVat || line.unitPrice)}</td><td className="px-3 py-2 text-right">{line.vatRate}%</td><td className="px-3 py-2 text-right font-semibold">{formatCurrency(line.lineTotalExVat)}</td></tr>
              ))}</tbody></table>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Totals</p>
        <div className="text-sm space-y-1.5" style={{maxWidth:'100%'}}>
          <div className="grid grid-cols-[1fr_auto] gap-2"><span className="text-gray-600">Subtotal excl. VAT</span><span className="font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(inv.subtotalExVat ?? inv.subtotalExclVat ?? 0)}</span></div>
          <div className="grid grid-cols-[1fr_auto] gap-2"><span className="text-gray-600">Discount</span><span className="text-gray-900 whitespace-nowrap">{(inv.discountTotal || 0) > 0 ? `-${formatCurrency(inv.discountTotal)}` : formatCurrency(0)}</span></div>
          <div className="grid grid-cols-[1fr_auto] gap-2"><span className="text-gray-600">Shipping excl. VAT</span><span className="text-gray-900 whitespace-nowrap">{formatCurrency(inv.shippingTotal ?? inv.shippingExVat ?? 0)}</span></div>
          <div className="grid grid-cols-[1fr_auto] gap-2"><span className="text-gray-600">VAT total</span><span className="text-gray-900 whitespace-nowrap">{formatCurrency(inv.vatTotal ?? 0)}</span></div>
          <div className="grid grid-cols-[1fr_auto] gap-2 border-t border-gray-200 pt-1.5 font-semibold"><span className="text-gray-900">Total incl. VAT</span><span className="text-gray-900 whitespace-nowrap">{formatCurrency(inv.totalInclVat ?? 0)}</span></div>
        </div>
      </div>
      {(inv.reviewReasons?.length > 0 || inv.rawImportMeta?.parseWarnings?.length > 0) && (
        <div className="bg-white rounded-lg border border-amber-200 p-4">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Validation</p>
          {inv.reviewReasons?.map((r, i) => <p key={`${i}-${r}`} className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{r}</p>)}
          {inv.rawImportMeta?.parseWarnings?.map((w, i) => <p key={`w-${i}-${w}`} className="text-xs text-gray-500">{w}</p>)}
        </div>
      )}
      {external && (
        <div className="bg-teal-50 rounded-lg border border-teal-200 p-4" data-testid="external-resolution-detail">
          <p className="text-sm font-semibold text-teal-900">Extern verwerkt in Yuki</p>
          <div className="text-xs space-y-1 mt-2 break-words">
            <p>DocumentID: <span data-testid="external-document-id">{external.documentId}</span></p>
            <p>Referentie: {external.yukiReference || external.invoiceNumber}</p>
            <p>Administratie: {external.administrationId}</p>
            <p>Boekhoudkundige status: {external.yukiStatus || 'Niet vastgelegd'}</p>
            <p>Bronverwijzing: {external.evidenceReference || 'Niet vastgelegd'}</p>
            <p>Oorspronkelijke PDF: {inv.rawImportMeta?.filename || 'Zie brondossier'}</p>
            {external.sourcePdfSHA256 && <p>PDF SHA256: {external.sourcePdfSHA256}</p>}
            <p>Registratie: {external.audit?.actor || 'Niet vastgelegd'} · {external.audit?.at || 'Niet vastgelegd'}</p>
            {external.audit?.note && <p>{external.audit.note}</p>}
            <p>Klantdocument: {external.customerDocumentStatus === 'unknown' || !external.customerDocumentStatus ? 'Niet vastgesteld' : external.customerDocumentStatus}</p>
            <p>Peppol-verzending: {external.peppolStatus === 'unknown' || !external.peppolStatus ? 'Niet vastgesteld' : external.peppolStatus}</p>
            <p>E-mailverzending: {external.emailStatus === 'unknown' || !external.emailStatus ? 'Niet vastgesteld' : external.emailStatus}</p>
            <p className="pt-2 font-semibold">Alleen de boekhoudkundige verwerking is geregistreerd. Commerce heeft deze externe verwerking niet geëxporteerd. Export en retry zijn uitgesloten.</p>
          </div>
        </div>
      )}
      {inv.export && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">{external ? 'Oorspronkelijke Commerce-poging' : 'Export'}</p>
          <div className="text-xs space-y-1">
            <p>Yuki: <span className={`font-semibold px-1.5 py-0.5 rounded ${EXP_STATUS_COLORS[inv.export.status]}`}>{isBookedWithoutPeppol(inv.export, inv.peppolDeliveryStatus) ? 'Geboekt in Yuki — zonder Peppol' : inv.export.status}</span></p>
            {inv.export.externalDocumentId && <p>Yuki Doc: {inv.export.externalDocumentId}</p>}
            {inv.export.lastError && <p className="text-red-600">Error: {inv.export.lastError}</p>}
          </div>
        </div>
      )}
      {exportHistory.length > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="invoice-export-history">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Exporthistoriek</p>
          <ul className="space-y-2 text-xs">
            {exportHistory.map(attempt => (
              <li key={attempt._id} data-testid={`export-history-${attempt._id}`}>
                <span className="font-semibold">{attempt.status}</span> · {attempt.created_at}
                {attempt.operatorOverride === true && <span>{isBookedWithoutPeppol(attempt) ? ' · operatoractie zonder Peppol' : ' · operatoractie'}</span>}
                {attempt.lastError && <p className="text-red-700">{attempt.lastError}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {inv.paidLegacySafety && (() => {
        const s = inv.paidLegacySafety;
        const colors = {
          green: 'border-emerald-200 bg-emerald-50',
          yellow: 'border-amber-200 bg-amber-50',
          red: 'border-red-300 bg-red-50',
        };
        const dot = {
          green: 'bg-emerald-500',
          yellow: 'bg-amber-500',
          red: 'bg-red-600',
        };
        const headline = {
          green: 'Payment signal present',
          yellow: 'Payment status uncertain — review required',
          red: 'Export blocked — settlement signal missing',
        };
        const fmt = (n) => (n === null || n === undefined ? '—' : formatCurrency(n));
        return (
          <div
            data-testid="paid-legacy-safety-card"
            className={`rounded-lg border p-4 ${colors[s.badge] || colors.yellow}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-700 uppercase font-semibold">Payment Safety</p>
              <span
                data-testid={`paid-legacy-safety-badge-${s.badge || 'yellow'}`}
                className="flex items-center gap-1 text-xs font-semibold text-gray-800">
                <span className={`inline-block w-2 h-2 rounded-full ${dot[s.badge] || dot.yellow}`} />
                {headline[s.badge] || 'Unknown'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <span className="text-gray-600">Invoice total</span>
              <span className="text-gray-900 text-right" data-testid="psl-invoice-total">{fmt(s.invoiceTotal)}</span>
              <span className="text-gray-600">Paid amount</span>
              <span className="text-gray-900 text-right" data-testid="psl-paid-amount">{fmt(s.paidAmount)}</span>
              <span className="text-gray-600">Amount due</span>
              <span className={`text-right font-semibold ${(s.amountDue ?? 0) > 0 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="psl-amount-due">{fmt(s.amountDue)}</span>
              <span className="text-gray-600">Payment status (canonical)</span>
              <span className="text-gray-900 text-right" data-testid="psl-payment-status">{s.paymentStatusRaw || '—'} <span className="text-gray-500">({s.normalisedStatus})</span></span>
              <span className="text-gray-600">Payment method</span>
              <span className="text-gray-900 text-right" data-testid="psl-payment-method">{s.paymentMethod || '—'}</span>
              <span className="text-gray-600">Paid at</span>
              <span className="text-gray-900 text-right" data-testid="psl-paid-at">{s.paidAt || '—'}</span>
              <span className="text-gray-600">Settlement signal in payload</span>
              <span className={`text-right font-semibold ${s.payloadIndicatesSettled ? 'text-emerald-700' : 'text-red-700'}`} data-testid="psl-settlement-signal">{s.payloadIndicatesSettled ? 'present' : 'missing'}</span>
            </div>
            {s.warnings?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                {s.warnings.map((w, i) => (
                  <p key={`psl-w-${i}`} data-testid={`psl-warning-${i}`} className="text-xs text-red-700 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </p>
                ))}
              </div>
            )}
            {s.badge === 'red' && (
              <p data-testid="psl-block-message" className="mt-2 text-xs font-semibold text-red-700 flex items-start gap-1">
                <X className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Export blocked / review required — no override available from this view.</span>
              </p>
            )}
          </div>
        );
      })()}
      {inv.contactIntegrity && (() => {
        const c = inv.contactIntegrity;
        const colors = {
          green: 'border-emerald-200 bg-emerald-50',
          yellow: 'border-amber-200 bg-amber-50',
          red: 'border-red-300 bg-red-50',
        };
        const dot = {
          green: 'bg-emerald-500',
          yellow: 'bg-amber-500',
          red: 'bg-red-600',
        };
        const headline = {
          green: 'Canonical customer matches Yuki contact',
          yellow: 'Yuki contact not yet known',
          red: 'Customer mismatch — review before export',
        };
        return (
          <div
            data-testid="contact-integrity-card"
            className={`rounded-lg border p-4 ${colors[c.badge] || colors.yellow}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-700 uppercase font-semibold">Customer Integrity</p>
              <span
                data-testid={`contact-integrity-badge-${c.badge || 'yellow'}`}
                className="flex items-center gap-1 text-xs font-semibold text-gray-800">
                <span className={`inline-block w-2 h-2 rounded-full ${dot[c.badge] || dot.yellow}`} />
                {headline[c.badge] || 'Unknown'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <span className="text-gray-600">Canonical customer</span>
              <span className="text-gray-900 text-right" data-testid="ci-canonical-customer">{c.canonicalCustomerNameMasked || '—'}</span>
              <span className="text-gray-600">Canonical VAT</span>
              <span className="text-gray-900 text-right" data-testid="ci-canonical-vat">{c.canonicalVatNumberMasked || '—'}</span>
              <span className="text-gray-600">Canonical address</span>
              <span className="text-gray-900 text-right" data-testid="ci-canonical-address">
                {[c.canonicalBillingAddress?.postalCode, c.canonicalBillingAddress?.city, c.canonicalBillingAddress?.country]
                  .filter(Boolean).join(' · ') || '—'}
              </span>
              <span className="text-gray-600">Yuki contact (last response)</span>
              <span className="text-gray-900 text-right" data-testid="ci-yuki-contact">{c.yukiContactNameMasked || '—'}</span>
            </div>
            <p data-testid="ci-message" className={`mt-2 text-xs ${c.badge === 'red' ? 'font-semibold text-red-700' : 'text-gray-700'}`}>
              {c.message}
            </p>
            {c.badge === 'red' && (
              <p data-testid="ci-block-message" className="mt-1 text-xs font-semibold text-red-700 flex items-start gap-1">
                <X className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Review required — no override available from this view.</span>
              </p>
            )}
          </div>
        );
      })()}
      {!external && inv.peppolEligibility && (
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="peppol-eligibility-card">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Peppol</p>
          <div className="text-xs space-y-1">
            {inv.peppolDeliveryStatus?.status === 'skipped_unavailable' ? (
              <p data-testid="peppol-badge-skipped" className="font-semibold text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Peppol skipped — recipient cannot currently receive via Peppol
              </p>
            ) : inv.export?.status === 'FAILED' && inv.export?.lastErrorType === 'peppol_unavailable' ? (
              <p data-testid="peppol-badge-unavailable" className="font-semibold text-amber-700">
                Ontvanger niet bereikbaar via Peppol. Boeking in Yuki niet bevestigd.
              </p>
            ) : inv.peppolDeliveryStatus?.status === 'sent' ? (
              <p data-testid="peppol-badge-sent" className="font-semibold text-emerald-700 flex items-center gap-1">
                <Check className="w-3 h-3" /> Peppol: sent on Yuki export
              </p>
            ) : inv.peppolEligibility.shouldSendToPeppol ? (
              <p data-testid="peppol-badge-yes" className="font-semibold text-emerald-700 flex items-center gap-1">
                <Check className="w-3 h-3" /> Peppol: yes — auto-included on Yuki export
              </p>
            ) : inv.peppolEligibility.facts?.resolvedContactType === 'Company' ? (
              <p data-testid="peppol-badge-warning" className="font-semibold text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Peppol: no — B2B detected but {inv.peppolEligibility.reasons?.[0] || 'incomplete data'}
              </p>
            ) : (
              <p data-testid="peppol-badge-no" className="text-gray-600 flex items-center gap-1">
                <X className="w-3 h-3" /> Peppol: no (B2C / not eligible)
              </p>
            )}
            {inv.peppolDeliveryStatus?.status === 'skipped_unavailable' && inv.peppolDeliveryStatus?.error && (
              <p data-testid="peppol-skipped-detail" className="text-xs text-gray-500">
                Yuki: <span className="italic">{inv.peppolDeliveryStatus.error}</span>
              </p>
            )}
            {inv.peppolEligibility.warnings?.map((w, i) => (
              <p key={`pw-${i}-${w}`} className="text-xs text-amber-600">⚠ {w}</p>
            ))}
          </div>
        </div>
      )}
      {!external && <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
        {!inv.archived && canEdit && <button onClick={startEdit} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit Invoice</button>}
        {!inv.archived && canEdit && inv.rawImportMeta?.rawText && <button onClick={() => onAction('reparse')} className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold hover:bg-gray-50 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reparse PDF</button>}
        {!inv.archived && inv.rawImportMeta?.rawText && (
          <>
            <button
              data-testid="reparse-address-dry-run-btn"
              onClick={() => onAction('reparseAddressDryRun')}
              className="px-3 py-1.5 border border-blue-200 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-50 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Adres-reparse (dry-run)
            </button>
            <button
              data-testid="reparse-address-apply-btn"
              onClick={() => onAction('reparseAddressApply')}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 flex items-center gap-1"
            >
              <Save className="w-3 h-3" /> Adres-reparse toepassen
            </button>
            <button
              data-testid="reparse-customer-name-dry-run-btn"
              onClick={() => onAction('reparseCustomerNameDryRun')}
              className="px-3 py-1.5 border border-emerald-200 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-50 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Klantnaam-resync (dry-run)
            </button>
            <button
              data-testid="reparse-customer-name-apply-btn"
              onClick={() => onAction('reparseCustomerNameApply')}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 flex items-center gap-1"
            >
              <Save className="w-3 h-3" /> Klantnaam-resync toepassen
            </button>
          </>
        )}
        {!inv.archived && ['needs_review', 'parsed'].includes(inv.status) && <button onClick={() => onAction('approve')} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 flex items-center gap-1"><Check className="w-3 h-3" /> Approve</button>}
        {!inv.archived && ['confirmed', 'approved'].includes(inv.status) && !inv.export?.status && <button onClick={() => onAction('export')} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 flex items-center gap-1"><Play className="w-3 h-3" /> Export Now</button>}
        {!inv.archived && inv.export?.status === 'FAILED' && inv.status !== 'duplicate_blocked' && inv.export?.retryDecision?.standardRetryAllowed === true && <button onClick={() => onAction('retry', inv.export._id)} className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Retry Export</button>}
        {/* Only the separate server-side operator decision offers this action.
            Archive ambiguity is diagnostic information, never retry authority. */}
        {!inv.archived
          && inv.export?.status === 'FAILED'
          && inv.status !== 'duplicate_blocked'
          && inv.export?.lastErrorType === 'peppol_unavailable'
          && inv.export?.operatorFallback?.allowed === true
          && inv.paidLegacySafety?.badge !== 'red'
          && inv.contactIntegrity?.badge !== 'red'
          && (
            <button data-testid="operator-fallback-btn"
              onClick={() => onAction('operatorFallback', inv.export._id)}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Exporteren zonder Peppol
            </button>
          )
        }
        {!inv.archived
          && inv.export?.status === 'FAILED'
          && !(inv.export?.retryDecision?.standardRetryAllowed === true)
          && !(inv.export?.operatorFallback?.allowed === true)
          && (
            <span data-testid="retry-gated-note" className="px-3 py-1.5 border border-amber-300 text-amber-800 rounded-lg text-xs font-semibold flex items-center gap-1 bg-amber-50" title={inv.export?.retryDecision?.blockedReason || 'Geen backend-retrybeslissing beschikbaar — fail-closed.'}>
              <X className="w-3 h-3" /> Retry geblokkeerd: {inv.export?.retryDecision?.blockedCode || 'decision_missing'}
            </span>
          )
        }
        {!inv.archived && inv.export?.status === 'FAILED' && inv.status === 'duplicate_blocked' && <span data-testid="retry-blocked-note" className="px-3 py-1.5 border border-red-200 text-red-700 rounded-lg text-xs font-semibold flex items-center gap-1 bg-red-50"><X className="w-3 h-3" /> Retry blocked</span>}
        {!inv.archived && inv.status !== 'duplicate_blocked' && inv.status !== 'exported' && <button onClick={() => onAction('block')} className="px-3 py-1.5 border border-red-200 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-50 flex items-center gap-1"><X className="w-3 h-3" /> Block</button>}
        {!inv.archived && ['duplicate_blocked', 'exported'].includes(inv.status) && <button onClick={() => onAction('reopen')} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reopen</button>}
        {!inv.archived && <button data-testid="archive-invoice-btn" onClick={() => onAction('archive')} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 flex items-center gap-1">Archive</button>}
        {inv.archived && <button data-testid="restore-invoice-btn" onClick={() => onAction('restore')} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Restore</button>}
      </div>}
    </div>
  );
}


export function OperatorFallbackConfirmation({ preview, running, onConfirm, onCancel }) {
  const [confirmedAbsent, setConfirmedAbsent] = useState(false);
  const dialogRef = useRef(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); previousFocus?.focus?.(); };
  }, []);
  const cancel = event => {
    event.preventDefault();
    if (!running) onCancel();
  };
  return (
    <dialog ref={dialogRef} onCancel={cancel} aria-labelledby="operator-fallback-title"
      className="w-full max-w-lg rounded-xl border border-gray-200 p-6 shadow-xl backdrop:bg-black/40"
      data-testid="operator-fallback-modal">
      <h2 id="operator-fallback-title" className="text-lg font-semibold">Exporteren zonder Peppol</h2>
      <p className="mt-3 text-sm text-gray-700">
        Deze factuur kon niet via Peppol worden afgeleverd.<br />
        Controleer dat ze niet reeds in Yuki aanwezig is.<br />
        Na bevestiging wordt dezelfde factuur naar Yuki gestuurd zonder Peppol.
      </p>
      <p className="mt-3 font-mono font-semibold">Factuur {preview.invoiceNumber}</p>
      <label className="mt-4 flex items-start gap-2 text-sm">
        <input type="checkbox" checked={confirmedAbsent} disabled={running}
          onChange={event => setConfirmedAbsent(event.target.checked)} data-testid="operator-fallback-absence-checkbox" />
        <span>Ik heb gecontroleerd dat factuur {preview.invoiceNumber} niet reeds in Yuki aanwezig is.</span>
      </label>
      <p className="mt-3 text-xs text-gray-500">Je bevestiging wordt geregistreerd. Dit vraagt geen Peppol-aflevering aan. Er volgt geen automatische tweede poging.</p>
      <div className="mt-5 flex gap-2">
        <button type="button" disabled={!confirmedAbsent || running} onClick={() => onConfirm(confirmedAbsent)}
          data-testid="operator-fallback-confirm" className="px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold text-sm disabled:opacity-50">
          {running ? 'Bezig…' : 'Exporteren zonder Peppol'}
        </button>
        <button type="button" disabled={running} onClick={onCancel} data-testid="operator-fallback-cancel"
          className="px-4 py-2 rounded-lg border border-gray-300 text-sm">Annuleren</button>
      </div>
    </dialog>
  );
}
